// =============================================================================
// hooks/sales/useAppointmentLeadSummaries.ts — Nome do lead de cada
// compromisso da agenda comercial (F33-S05).
//
// `contact_appointments` só guarda `leadId` (doc 26 §8 — nunca denormaliza
// nome, para não duplicar fonte de verdade). A tela de agenda precisa exibir
// "cliente" por linha, então resolve o nome com GET /api/leads/:id por lead
// — MESMA query key de hooks/crm/useLead.ts (`['leads','detail',id]`), então
// o cache é compartilhado com o CRM: quem já abriu o lead no CRM não paga a
// requisição de novo aqui, e vice-versa.
//
// Escala: aceitável para o volume do piloto comercial (dezenas de
// compromissos por página, nunca milhares — a lista é paginada). Se o
// módulo crescer, o follow-up é a API de agenda devolver o nome já
// denormalizado (ou um lookup em lote) — não duplicar essa decisão aqui.
//
// Resiliência: `leads:read` não é uma das permissões do módulo comercial
// (doc 26 §12) — um ator que só tenha `sales_appointments:read` pode não
// conseguir ler o lead. Cada falha fica isolada (não derruba a lista de
// compromissos); o card mostra um estado de fallback.
// =============================================================================

import type { LeadResponse } from '@elemento/shared-schemas';
import { useQueries } from '@tanstack/react-query';

import { api } from '../../lib/api';

// Mesma chave de hooks/crm/useLead.ts — mantida aqui como cópia literal (não
// importável sem criar dependência cruzada de features/crm em hooks/sales).
const LEAD_QUERY_KEY = (id: string): readonly [string, string, string] =>
  ['leads', 'detail', id] as const;

async function fetchLeadSummary(id: string): Promise<LeadResponse> {
  return api.get<LeadResponse>(`/api/leads/${encodeURIComponent(id)}`);
}

// Só `id`+`name` — `phoneE164` foi removido por não ter consumidor (nenhum
// componente da agenda exibe telefone): princípio da necessidade, LGPD §14.2
// (achado de revisão F33-S05). Se algum cartão precisar do telefone no
// futuro, reintroduza aqui em vez de buscar por fora deste hook.
export interface AppointmentLeadSummary {
  id: string;
  name: string;
}

export interface UseAppointmentLeadSummariesResult {
  /** leadId → resumo. Ausente = ainda carregando OU falhou (ver `failedIds`). */
  summaries: Map<string, AppointmentLeadSummary>;
  isLoading: boolean;
  /** leadIds que falharam ao resolver (403/404/rede) — UI mostra fallback, não erro de página. */
  failedIds: Set<string>;
}

/**
 * Resolve nome + telefone de um conjunto de leads, um GET por id (deduplicado
 * pela query key entre renders/telas). Nunca lança — falha por lead vira
 * `failedIds`, o card cai no fallback (doc 26 §9 "Estados obrigatórios").
 */
export function useAppointmentLeadSummaries(leadIds: string[]): UseAppointmentLeadSummariesResult {
  const uniqueIds = Array.from(new Set(leadIds));

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: LEAD_QUERY_KEY(id),
      queryFn: () => fetchLeadSummary(id),
      staleTime: 30_000,
      retry: false,
    })),
  });

  const summaries = new Map<string, AppointmentLeadSummary>();
  const failedIds = new Set<string>();
  let isLoading = false;

  results.forEach((result, index) => {
    const id = uniqueIds[index];
    if (id === undefined) return;

    if (result.isLoading) {
      isLoading = true;
      return;
    }
    if (result.isError || result.data === undefined) {
      failedIds.add(id);
      return;
    }
    summaries.set(id, {
      id: result.data.id,
      name: result.data.name,
    });
  });

  return { summaries, isLoading, failedIds };
}
